import { StringUtils } from '@/common/utils/StringUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';

export const DimRearrangeDialog = ({ codeToDim, activeDims, numFrozenDims = 0, closeDialog }) => {
  const [hiddenDimCodes, setHiddenDimCodes] = useState(() => {
    const allDimCodes = Object.keys(codeToDim);
    const activeDimCodes = activeDims.map((dim) => dim.code);
    return allDimCodes.filter((dimCode) => !activeDimCodes.includes(dimCode));
  });
  const [activeDimCodes, setActiveDimCodes] = useState(activeDims.map((dim) => dim.code));

  const submit = () => {
    closeDialog(activeDimCodes);
  };

  const handleDragEnd = ({ source, destination, draggableId }) => {
    // dropped outside any list
    if (!destination) {
      return;
    }

    // dropped above a frozen dim
    if (destination.droppableId === 'active' && destination.index < numFrozenDims) {
      return;
    }

    // same list reordering
    if (source.droppableId === destination.droppableId) {
      if (source.droppableId === 'hidden') {
        const newHidden = Array.from(hiddenDimCodes);
        newHidden.splice(source.index, 1);
        newHidden.splice(destination.index, 0, draggableId);
        setHiddenDimCodes(newHidden);
      } else {
        const newActive = Array.from(activeDimCodes);
        newActive.splice(source.index, 1);
        newActive.splice(destination.index, 0, draggableId);
        setActiveDimCodes(newActive);
      }
      return;
    }

    // moving between lists
    const sourceList = source.droppableId === 'hidden' ? hiddenDimCodes : activeDimCodes;
    const destList = destination.droppableId === 'hidden' ? hiddenDimCodes : activeDimCodes;
    const setSource = source.droppableId === 'hidden' ? setHiddenDimCodes : setActiveDimCodes;
    const setDest = destination.droppableId === 'hidden' ? setHiddenDimCodes : setActiveDimCodes;

    // remove from source
    const newSource = Array.from(sourceList);
    newSource.splice(source.index, 1);
    setSource(newSource);

    // add to destination
    const newDest = Array.from(destList);
    newDest.splice(destination.index, 0, draggableId);
    setDest(newDest);
  };

  const theme = useTheme();

  const renderDropColumn = (id, dimCodes) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', minWidth: 0, minHeight: 350 }}>
        <Typography
          align="center"
          variant="subtitle1"
          sx={{ color: 'text.secondary', mb: 1 }}
        >{`${StringUtils.toUpperCamel(id)} Dimensions`}</Typography>
        <Droppable droppableId={id}>
          {(provided, snapshot) => {
            return (
              <List
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{
                  bgcolor: theme.palette.surface.low,
                  height: '100%',
                  borderRadius: theme.shape.borderRadius / 4 + 'px',
                  p: 1,
                  border: 1,
                  borderColor: snapshot.isDraggingOver ? alpha(theme.palette.surface.tint, 0.16) : 'transparent',
                  transition: 'border-color 0.2s ease',
                }}
              >
                {dimCodes.map((dimCode, index) => {
                  const isFrozen = id === 'active' && index < numFrozenDims;
                  return (
                    <Draggable key={dimCode} draggableId={dimCode} index={index} isDragDisabled={isFrozen}>
                      {(prov, snapshot) => (
                        <>
                          <ListItem
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            sx={{
                              mb: 1,
                              borderRadius: '4px',
                              color: isFrozen ? theme.palette.action.disabled : theme.palette.text.primary,
                              bgcolor:
                                isFrozen ? theme.palette.action.disabledBackground
                                : snapshot.isDragging ? alpha(theme.palette.surface.tint, 0.16)
                                : theme.palette.surface.container,
                              boxShadow: snapshot.isDragging ? 3 : 0,
                              transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                              '&:hover': {
                                cursor: isFrozen ? 'default' : 'grab',
                                bgcolor:
                                  isFrozen ?
                                    theme.palette.action.disabledBackground
                                  : alpha(theme.palette.surface.tint, 0.08),
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <FontAwesomeIcon
                                style={{
                                  color: isFrozen ? theme.palette.action.disabled : theme.palette.text.secondary,
                                }}
                                icon={fas.faGripVertical}
                              />
                            </ListItemIcon>
                            <ListItemText primary={codeToDim[dimCode].displayName} />
                          </ListItem>
                        </>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </List>
            );
          }}
        </Droppable>
      </Box>
    );
  };

  return (
    <Dialog onClose={() => closeDialog()} open fullWidth maxWidth="md" data-test-id="dim-rearrange-dialog">
      <DialogTitle>Rearrange Dimensions</DialogTitle>

      <DialogContent>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Box
            sx={{
              flex: 1, // takes up remaining vertical space
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              overflowY: 'auto',
              padding: 2,
            }}
          >
            {/* Hidden Dims */}
            {renderDropColumn('hidden', hiddenDimCodes)}

            {/* Active Dims */}
            {renderDropColumn('active', activeDimCodes)}
          </Box>
        </DragDropContext>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={() => closeDialog()}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={submit} data-test-id="dim-rearrange-submit-button">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
